/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { t } from '../../translations/i18n';
import { Feature, FeatureCollection } from 'geojson';
import { store } from '../../store/store';
import { useSelector } from 'react-redux';
import Button from '@mui/material/Button';
import Checkmark from '../../icons/Checkmark.svg?react';
import { MmgOperationContainer } from '../../shared/operations/operation-container';
import { OPERATION_TOOL_IDS } from './operation-constants';
import { EMapToolActionType } from '../../store/actions/MapToolActionType';
import { 
  EDIT_MODE,
  EDIT_MODE_DELETED,
  EDIT_MODE_MODIFIED,
} from '../../store/reducers/EditReducer';
import { buttonActiveStyle } from '../edit-modes/select-for-editing-modes';
import { GEOMETRY_ITEM_TYPES } from '../../models/IGeometries';
import mikeSharedTheme from '../styles/mikeSharedTheme';
import MIKE_COLORS from '../styles/mike-colors';
import MikeVisualizer from '../../MikeVisualizer';
import { IGlobalState } from '../../store/reducers';
import OverlayOp from  'jsts/org/locationtech/jts/operation/overlay/OverlayOp'
import GeometryFactory from 'jsts/org/locationtech/jts/geom/GeometryFactory';
import GeoJSONReader from 'jsts/org/locationtech/jts/io/GeoJSONReader';
import GeoJSONWriter from 'jsts/org/locationtech/jts/io/GeoJSONWriter';
import Geometry from 'jsts/org/locationtech/jts/geom/Geometry';

const OPERATION_INPUT_STYLES = css`
  display: flex;
  align-items: center;
  overflow: hidden;
  padding-left: ${mikeSharedTheme.spacing(2)};
  padding-right: ${mikeSharedTheme.spacing(2)};
`;

const OPERATION_DESCRIPTION_STYLES = css`
  color: ${MIKE_COLORS.DARKGREY_DEFAULT};
  font-size: 12px;
`;

const PADDING_STYLES = css`
  padding-left: ${mikeSharedTheme.spacing(2)};
  padding-right: ${mikeSharedTheme.spacing(2)};
`;

const {
  setDrawVectorLayerGeojson,
  clearDrawnVectorLayerData,
  onDrawnDataUpdated,
  enable2DPolygonDrawingV2,
  disable2DPolygonDrawing,
  getCurrentlyDrawnGeojson
} = MikeVisualizer;

const emptyFeatureCollection: FeatureCollection<any, any> = {
  type: 'FeatureCollection',
  features: [],
};

type ClipOperationProps = {
  disabled?: boolean;
  setOperationDone: (fc: FeatureCollection<any, any>, mixedEditModeSet: boolean) => void;
};

/**
 * @name MmgConnectedClipOperation
 * @summary Clips currently drawn geojson by additionally drawn clip polygon(s).
 *
 * @param props
 */
export function MmgConnectedClipOperation(props: ClipOperationProps) {
  const { disabled, setOperationDone } = props;
  const { polygonDrawingEnabled } = useSelector((state: IGlobalState) => state.MapToolReducer);
  const activeOperationToolId = useSelector(
    (state: IGlobalState) => state.GeometryClientOperationReducer.activeOperationToolId,
  );

  const { geometryEditItemType } = useSelector((state: IGlobalState) => state.EditReducer);

  const hint = useMemo(
    () => {
      switch (geometryEditItemType) {
        case GEOMETRY_ITEM_TYPES.LINE_STRING:
        case GEOMETRY_ITEM_TYPES.MULTI_LINE_STRING:
          return t('OP_CLIPOPERATIONUIDEFINITION_DESCRIPTION_LINE');
        case GEOMETRY_ITEM_TYPES.POLYGON:
        case GEOMETRY_ITEM_TYPES.MULTI_POLYGON:
          return t('OP_CLIPOPERATIONUIDEFINITION_DESCRIPTION_POLYGON');
      }
    },
    [geometryEditItemType],
  );

  const isOpen = useMemo(
    () => {
      return activeOperationToolId === OPERATION_TOOL_IDS.CLIP;
    },
    [activeOperationToolId],
  );

  const [clippingFC, setClippingFC] = useState({
    ...emptyFeatureCollection,
  });

  const [selectedFC, setSelectedFC] = useState({
    ...emptyFeatureCollection,
  });

  const clipToggled = (open: boolean) => {
    if (open) {
      store.dispatch({
        type: 'clientOperation/SET_ACTIVE_TOOL',
        operationToolId: OPERATION_TOOL_IDS.CLIP,
      });
    } else {
      store.dispatch({ type: 'clientOperation/EXIT_ACTIVE_TOOL' });
    }
  };

  const acceptClip = useCallback(
    async () => {
      const getEditMode = (/* origGeometry: Geometry, */ processedGeometry: Geometry) => {
        if (geometryEditItemType === GEOMETRY_ITEM_TYPES.POLYGON || geometryEditItemType === GEOMETRY_ITEM_TYPES.MULTI_POLYGON){
          const processedArea = processedGeometry.getArea();
          return processedArea === 0 ? EDIT_MODE_DELETED : EDIT_MODE_MODIFIED
          /* if (processedArea === 0){
            return EDIT_MODE_DELETED
          }
          return processedArea === 0 ? EDIT_MODE_DELETED : EDIT_MODE_MODIFIED
          const areaOrig = origGeometry.getArea();
          return areaOrig !== processedArea ? EDIT_MODE_MODIFIED : ""     */      
        }
        else{
          const processedLength = processedGeometry.getLength();
          return processedLength === 0 ? EDIT_MODE_DELETED : EDIT_MODE_MODIFIED
          /* if (processedLength === 0){
            return EDIT_MODE_DELETED
          }
          const origLength = origGeometry.getLength();          
          return origLength !== processedLength ? EDIT_MODE_MODIFIED : ""  */
        }
      }
      clearDrawnVectorLayerData();
      store.dispatch({ type: 'clientOperation/EXIT_ACTIVE_TOOL' });
      
      const drawnClippingPolygons = clippingFC.features.filter((f: Feature<any, any>) => {
        const keys = Object.keys(f.properties);
        return !keys.includes(EDIT_MODE);
      });

      const geomsToClip = selectedFC.features.filter((f: Feature<any, any>) => {
        const keys = Object.keys(f.properties);
        return keys.includes(EDIT_MODE);
      });

      const geometryFactory = new GeometryFactory();              
      const geojsonRader = new GeoJSONReader(geometryFactory);  
      const geojsonWriter = new GeoJSONWriter();      
      const clippingFeature = drawnClippingPolygons[0]
      const jstsClippingGeometry = geojsonRader.read(clippingFeature.geometry);
      const processedFeatures = Array<Feature<any>>()
      const modifiedFeatures = Array<Feature<any>>()
      const overlayType = 1;  // 1 --> DIFFERENCE 3 --> INTERSECTION   
      geomsToClip.forEach((feature) => { 
        const jstsGeometryToBeClipped = geojsonRader.read(feature.geometry);   
        const intersectionOp = new OverlayOp(jstsGeometryToBeClipped, jstsClippingGeometry) 
        const result = intersectionOp.getResultGeometry(overlayType)     
        if (result){ 
          const editMode = getEditMode(/* jstsGeometryToBeClipped,  */result);   
          if (editMode){
            const clippedGeometry = geojsonWriter.write(result)           
            const newProperties = {...feature.properties, [EDIT_MODE]: editMode}          
            const newFeature = editMode === EDIT_MODE_DELETED ? {...feature, properties: newProperties} :  {...feature, geometry: clippedGeometry, properties: newProperties}
            processedFeatures.push(newFeature)
            if (editMode !== EDIT_MODE_DELETED){
              modifiedFeatures.push(newFeature)
            }
          }           
        }
      })
      setDrawVectorLayerGeojson({...selectedFC, features: modifiedFeatures});
      setOperationDone({...selectedFC, features: processedFeatures},true);
    },
    [clippingFC, selectedFC, setOperationDone, geometryEditItemType],
  );

  useEffect(
    () => {
      if (isOpen) {
        setSelectedFC(getCurrentlyDrawnGeojson());
        enable2DPolygonDrawingV2({singleItem: true});
        store.dispatch({ type: EMapToolActionType.ENABLE_POLYGON_DRAWING });
      }
      else{
        setClippingFC(emptyFeatureCollection)
        setSelectedFC(emptyFeatureCollection);
      }
    },
    [isOpen],
  );


  useEffect(
    () => {
      const drawUpdateCallback = (updatedFC: FeatureCollection<any, any>) => {
        if (isOpen && polygonDrawingEnabled) {
          setClippingFC(updatedFC);
        }
      };

      const unsubscribers = [];
      const unsubscribeOnDrawnDataUpdated = onDrawnDataUpdated(drawUpdateCallback);
      unsubscribers.push(unsubscribeOnDrawnDataUpdated);

      return () => {
        unsubscribers.forEach((unsubscribe) => unsubscribe());
        disable2DPolygonDrawing();
      };
    },
    [polygonDrawingEnabled, isOpen],
  );

  return (
    <MmgOperationContainer
      isOpen={isOpen}
      onContainerToggled={clipToggled}
      operationName={t('UI_CLIPOPERATION')}
      disabled={disabled}
    >
      <div css={OPERATION_INPUT_STYLES}>
        <div css={OPERATION_DESCRIPTION_STYLES}>{t('CLIP_DRAW_TOOLS_TITLE') + " " + hint}</div>
        <Button onClick={acceptClip} css={css`${buttonActiveStyle} ${PADDING_STYLES}`}>
          <Checkmark />
        </Button>       
      </div>
    </MmgOperationContainer>
  );
}
